/**
 * Backend Services Configuration
 * This file provides backward compatibility and uses centralized configuration
 */

import { getConfig, getBackendUrl, getKeycloakUrl, getModuleEndpoint as getModuleEndpointFromConfig } from './config.loader';

export interface ServiceEndpoint {
  baseUrl: string;
  apiVersion?: string;
  timeout?: number;
  retries?: number;
}

export interface ModuleRoutes {
  [key: string]: string;
}

export interface SecurityConfig {
  tokenStorageKey: string;
  refreshTokenKey: string;
  tokenExpiryBuffer: number; // minutes before expiry to refresh
  csrfEnabled: boolean;
  corsEnabled: boolean;
}

export interface ServicesConfig {
  backend: ServiceEndpoint;
  keycloak: ServiceEndpoint;
  modules: ModuleRoutes;
  security: SecurityConfig;
  environment: string;
}

// Load configuration from centralized config
function loadServicesConfig(): ServicesConfig {
  const appConfig = getConfig();
  
  // Flatten modules for backward compatibility
  const modules: ModuleRoutes = {};
  Object.values(appConfig.modules).forEach(categoryModules => {
    Object.assign(modules, categoryModules);
  });

  return {
    backend: {
      baseUrl: appConfig.backend.url,
      apiVersion: appConfig.backend.apiVersion,
      timeout: appConfig.backend.timeout,
      retries: appConfig.backend.retries
    },
    keycloak: {
      baseUrl: appConfig.keycloak.url,
      timeout: appConfig.keycloak.timeout,
      retries: appConfig.keycloak.retries
    },
    modules,
    security: appConfig.security,
    environment: appConfig.environment
  };
}

export const servicesConfig = loadServicesConfig();

// Helper function to build full URL
export function buildUrl(service: 'backend' | 'keycloak', path: string): string {
  if (service === 'backend') {
    return getBackendUrl(path);
  } else if (service === 'keycloak') {
    return getKeycloakUrl(path);
  }
  throw new Error(`Unknown service: ${service}`);
}

// Helper function to get module endpoint
export function getModuleEndpoint(module: keyof typeof servicesConfig.modules): string {
  const path = servicesConfig.modules[module];
  return getBackendUrl(path);
}

// Validation function
export function validateConfig(): boolean {
  const requiredFields = [
    servicesConfig.backend.baseUrl,
    servicesConfig.keycloak.baseUrl
  ];
  
  return requiredFields.every(field => field && field.length > 0);
}

// Security helpers
export function getStorageKeys() {
  return {
    accessToken: servicesConfig.security.tokenStorageKey,
    refreshToken: servicesConfig.security.refreshTokenKey
  };
}

export function shouldRefreshToken(expiryTime: number): boolean {
  const now = Date.now() / 1000;
  const bufferTime = servicesConfig.security.tokenExpiryBuffer * 60; // Convert to seconds
  return (expiryTime - now) <= bufferTime;
}